import { useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { BOM_STATUSES, Color, LIST_PAGES, Size, bomPills, documentPageMode, t, type BomCostChangeRecord, type BomLockReason, type BomSheetAnswer, type StatusResolver } from '@bms2/shared';
import { Button, Select, TextInput } from '../../components/controls';
import { useNotice } from '../../components/controls/useNotice';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { DealBomEditor, type BomEditorDivider, type BomEditorLine } from './DealBomEditor';
import { BannerPills } from './BannerPills';
import { DocumentActionBar, DocumentViewPage } from './DocumentViewPage';
import { documentEditStyles as editStyles } from './DocumentEditPage';
import { PageModule } from '../../shell/PageModule';
import { useStatusResolver } from '../../shell/StatusResolver';
import { patch, useApi, DEALS_PATH } from './api';
import styles from './DealBomList.module.css';
import { DocumentNotes } from './DocumentNotes';
import { AttachToDealModal } from './BomPickers';
import { BomCostChangeLine } from './BomCostChange';
import { PickToDealWindow } from './PickToDealWindow';

/** What `GET deals/boms/:bomId` answers: the list, its name and the Deal it is on (decision 0565). */
export interface BomPageAnswer {
  bomId: string;
  name: string;
  /** What the list is for, in the builder's own words; edited here (decision 0609). */
  description?: string | null;
  /** 🔒 The one Deal the page takes (decision 1090): the Deal the address names, else the newest attached, else null. */
  dealId: string | null;
  dealName: string;
  /** Every Deal the list is attached to, newest first (decision 1090); absent from an older reply. */
  deals?: { dealId: string; dealName: string; stage: string; customerName: string | null }[];
  /** True where every Deal the list is attached to is Lost: it is kept for reference and never edited (piece 18; decision 1090). */
  dealIsLost: boolean;
  /** 🔒 True where every Deal the list is attached to is done, Lost or Won by its stage (decisions 1466, 1512); absent from an older reply. */
  locked?: boolean;
  /** Why the list is locked, which the line above its read-only grid says (decision 1466). */
  lockReason?: BomLockReason | null;
  /** 🔒 The newest costed change after a Quote drawn from the list went out, drawn as a warning line (decision 1466). */
  costChange?: BomCostChangeRecord | null;
  /** 🔒 The list's own status, Draft or Final (decision 1091); absent from an older reply, which draws no pill. */
  status?: string;
  lines: BomEditorLine[];
  dividers: BomEditorDivider[];
  /** Whole cents (T2192 item 12); `costCents` is null for a viewer refused Item.purchasePrice. */
  costCents: number | null;
  floorCents: number;
  rfqRequired: boolean;
}

/**
 * 🔨 Relative to the API's own root, never prefixed with /api. 🔒 `dealId` is the
 * Deal the person reached the list from, which the answer takes as its one Deal
 * where the list is attached to it (decision 1090).
 */
export const BOM_PATH = (bomId: string, dealId?: string | null) => `${DEALS_PATH}/boms/${bomId}${dealId ? `?deal=${encodeURIComponent(dealId)}` : ''}`;
/** 🔒 The list's printed sheet (decision 0862): the page's PDF view reads the first, the download and Export PDF the second. */
export const BOM_SHEET_VIEW_PATH = (bomId: string, dealId?: string | null) => `${DEALS_PATH}/boms/${bomId}/print${dealId ? `?deal=${encodeURIComponent(dealId)}` : ''}`;
export const BOM_SHEET_PDF_PATH = (bomId: string, dealId?: string | null) => `${DEALS_PATH}/boms/${bomId}/pdf${dealId ? `?deal=${encodeURIComponent(dealId)}` : ''}`;

/**
 * One bill of materials on its own page, nested under the Deal it prices
 * (decision 0565). 🔒 Since decision 0862 it reads as every accounting document
 * reads (decision 0612): **PDF** is the list's own sheet through the one region
 * renderer, and **Details** is the list's facts, its description and its lines,
 * read-only since decision 1054. The owner's T127: *"RFQ and BOM is not
 * following the standard accounting document layout or PDF layout"*. 🔒 Edit at
 * the footer's right opens the Item Table editor the Deal's tile used to hold, at
 * `?mode=edit`, and Done returns (decision 1054).
 *
 * 🔨 The sheet is read on its own route, beside the list's answer the Deal's tile
 * reads too, so a tile unfolding a list renders no sheet. The page's view is
 * derived from the sheet's presence on every render, so a sheet landing second
 * still opens the page on it (decision 0612's review finding). 🔨 One page draws a
 * list however it was reached, beside the list of its own (decision 1052).
 */
export function DealBomPage({ bomId, apiBaseUrl = '' }: { bomId: string; apiBaseUrl?: string }) {
  // 🔒 The banner's tone rides the notice, so a refused change reads as an error and never as a success (T2099).
  const [noticeBanner, showNotice] = useNotice();
  const setNotice = (message: string | null) => showNotice(message === null ? null : { tone: 'good', text: message });
  const setRefusal = (message: string) => showNotice({ tone: 'error', text: message });
  const [dirty, setDirty] = useState(false);
  const [params, setParams] = useSearchParams();
  const asked = documentPageMode(params.get('mode'), bomId) === 'edit';
  const resolveStatus = useStatusResolver();
  /** 🔒 The Deal the address names, the one the page takes where the list is on it (decision 1092). */
  const reachedFrom = params.get('deal');
  const page = useApi<BomPageAnswer>(BOM_PATH(bomId, reachedFrom), apiBaseUrl);
  /** Attach to a Deal's picker (decision 1093); unmounted when closed. */
  const [attaching, setAttaching] = useState(false);
  /** Pick to Deal's window (decision 3322); unmounted when closed. */
  const [picking, setPicking] = useState(false);
  const sheet = useApi<BomSheetAnswer>(BOM_SHEET_VIEW_PATH(bomId, reachedFrom), apiBaseUrl);
  const drawn = sheet.state.kind === 'ready' ? sheet.state.data : null;
  // 🔒 A save changes what the sheet prints, so both reads run again.
  const reload = () => {
    page.reload();
    sheet.reload();
  };
  /** The mode rides the address as every document's does (decision 1054), so the frame's own path is kept and only `mode` moves. */
  const setEditing = (next: boolean) => {
    setNotice(null);
    setParams((was) => {
      const kept = new URLSearchParams(was);
      if (next) kept.set('mode', 'edit');
      else kept.delete('mode');
      return kept;
    });
  };
  const saved = (message: string) => {
    setNotice(message);
    reload();
  };
  return (
    <AsyncStatePanel state={page.state} emptyAction={t('deal.boms.loadFailed')} onRetry={page.reload}>
      {(answer) => {
        // 🔒 A list whose every Deal is done is locked (decision 1466, amending 1090), read off the answer's own lock.
        const live = answer.locked !== true && answer.dealIsLost !== true;
        const lockReason: BomLockReason = answer.lockReason ?? 'lost';
        // 🔨 A locked list never opens its editor, even where the address asks for it by hand (decision 1054).
        const editing = asked && live;
        // 🔒 The pill is the list's own status, and every attached Deal is named beside it as a link (decision 1091).
        const status = (
          <span className={styles.statusRow}>
            {answer.status ? <BannerPills pills={bomPills({ status: answer.status }, resolveStatus)} attribute="data-bom-pills" /> : null}
            <BomDealLinks deals={answer.deals ?? []} />
          </span>
        );
        // 🔒 The newest costed change after the Quote went out stands under the notice in both views (decision 1466).
        const notices = (
          <>
            {noticeBanner}
            <BomCostChangeLine bomId={answer.bomId} change={answer.costChange} />
          </>
        );
        const subtitle = answer.dealId ? `${t('deal.boms.onDeal')} ${answer.dealName}` : t('deal.boms.notAttached');
        if (editing) {
          return (
            <PageModule
              icon="quote"
              // 🔒 The title stays the list's own name: a bill of materials takes no number (decision 1054).
              title={answer.name}
              subtitle={subtitle}
              {...(status === null ? {} : { status })}
              footer={
                <DocumentActionBar
                  attributes={{ 'data-document-actions': 'BOM', 'data-bom-mode': 'edit' }}
                  edit={
                    <span data-bom-action="done">
                      {/* 🔨 Refused while the grid holds unsaved rows, so leaving never drops typing (decision 1054). */}
                      <Button size={Size.Medium} color={Color.Navy} text={[t('deal.boms.doneEditing')]} actions={[() => setEditing(false)]} disabled={dirty} />
                    </span>
                  }
                />
              }
            >
              <div className={editStyles.form} data-bom-page={answer.bomId} data-document-mode="edit">
                {notices}
                <section className={editStyles.section} data-document-header="true">
                  <h2 className={editStyles.heading}>{t('document.edit.header')}</h2>
                  {/* 🔒 What this list is for, edited where the list is (decision 0609). */}
                  <BomDescription key={answer.bomId} bomId={answer.bomId} description={answer.description ?? null} editable apiBaseUrl={apiBaseUrl} onSaved={saved} onError={setRefusal} />
                </section>
                <section className={editStyles.section} data-document-lines="true">
                  <h2 className={editStyles.heading}>{t('document.edit.lines')}</h2>
                  <DealBomEditor
                    bomId={answer.bomId}
                    dealId={answer.dealId}
                    lines={answer.lines}
                    dividers={answer.dividers}
                    seesCost={answer.costCents !== null}
                    editable
                    apiBaseUrl={apiBaseUrl}
                    onSaved={saved}
                    onError={setRefusal}
                    onDirtyChange={setDirty}
                  />
                </section>
              </div>
            </PageModule>
          );
        }
        // 🔒 One notes panel, placed in the Details view and handed to the PDF view's rail; only one view is mounted (T587; decision 1403).
        const notesPanel = <DocumentNotes recordType="BOM" recordId={bomId} recordLabel={answer.name} apiBaseUrl={apiBaseUrl} />;
        return (
          <DocumentViewPage
            notes={notesPanel}
            documentType="BOM"
            icon="quote"
            title={answer.name}
            subtitle={subtitle}
            {...(status === null ? {} : { status })}
            // 🔒 The deal this list belongs to and how long it is, on the page rather than in the title bar (T170).
            information={[
              // 🔒 The page's one Deal (decision 1090); a list on no Deal says so and links nowhere.
              answer.dealId ? { label: t('deal.boms.deal'), value: answer.dealName, route: `/deals/${answer.dealId}` } : { label: t('deal.boms.deal'), value: t('deal.boms.notAttached') },
              { label: t('deal.boms.lineCount'), value: String(answer.lines.length) },
            ]}
            pageAttributes={{ 'data-bom-page': answer.bomId, 'data-document-mode': 'view' }}
            sheet={drawn?.printSheet ?? null}
            layout={drawn?.printLayout ?? null}
            pdfPath={BOM_SHEET_PDF_PATH(answer.bomId, reachedFrom)}
            listRoute={LIST_PAGES.BOM.route}
            apiBaseUrl={apiBaseUrl}
            notices={notices}
            // 🔒 Edit at the footer's right opens the live editor (decision 1054); a Lost Deal's list offers none.
            {...(live ? { onEdit: () => setEditing(true) } : {})}
            details={
              <>
                {/* 🔒 Attach to a Deal (decision 1093): the Deals it is on are named beside the pill. */}
                <p className={styles.attachRow}>
                  <span data-bom-action="attach-deal">
                    <Button size={Size.Small} text={[t('deal.boms.attachToDeal')]} actions={[() => setAttaching(true)]} />
                  </span>
                  {/* 🔒 Pick to Deal (decision 3322): offered where the list has a From Stock line, on a locked list too, since a pick writes no line. A reader refused the Item module's cost (a Deal-only reader) is not offered it, since the Item module's write gate refuses the window (T2295). */}
                  {answer.costCents !== null && answer.lines.some((line) => line.source === 'FromStock') ? (
                    <span data-bom-action="pick-to-deal">
                      <Button size={Size.Small} text={[t('deal.bom.pick.open')]} actions={[() => setPicking(true)]} />
                    </span>
                  ) : null}
                </p>
                {picking ? (
                  <PickToDealWindow
                    bomId={answer.bomId}
                    dealId={answer.dealId}
                    apiBaseUrl={apiBaseUrl}
                    onCancel={() => setPicking(false)}
                    onPicked={() => {
                      setPicking(false);
                      saved(t('deal.bom.pick.done'));
                    }}
                  />
                ) : null}
                {attaching ? (
                  <AttachToDealModal
                    bomId={answer.bomId}
                    held={(answer.deals ?? []).map((deal) => deal.dealId)}
                    apiBaseUrl={apiBaseUrl}
                    onCancel={() => setAttaching(false)}
                    onAttached={() => {
                      setAttaching(false);
                      saved(t('deal.boms.attached'));
                    }}
                  />
                ) : null}
                {/* 🔒 The list's own status, set either way here (decision 1091); a locked list keeps it. */}
                {answer.status ? <BomStatusControl key={`${answer.bomId}-${answer.status}`} bomId={answer.bomId} status={answer.status} editable={live} resolve={resolveStatus} apiBaseUrl={apiBaseUrl} onSaved={saved} onError={setRefusal} /> : null}
                {/* 🔒 Details reads (decision 1054): the description as a sentence, the pencil being the editor's. */}
                <BomDescription key={answer.bomId} bomId={answer.bomId} description={answer.description ?? null} editable={false} apiBaseUrl={apiBaseUrl} onSaved={saved} onError={setRefusal} />
                <DealBomEditor
                  bomId={answer.bomId}
                  dealId={answer.dealId}
                  lines={answer.lines}
                  dividers={answer.dividers}
                  seesCost={answer.costCents !== null}
                  // 🔒 A live list reads on the grid alone; a Lost Deal's keeps its export and says why it is locked (piece 18; T200).
                  editable={false}
                  lockReason={lockReason}
                  wonDeals={(answer.deals ?? []).filter((deal) => deal.stage === 'Won').map((deal) => deal.dealName)}
                  readOnly={live}
                  apiBaseUrl={apiBaseUrl}
                  onSaved={saved}
                  onError={setRefusal}
                />
                {/* 🔒 The document's notes, the composer at the top of the list (decision 0951; T336). */}
                {notesPanel}
              </>
            }
          />
        );
      }}
    </AsyncStatePanel>
  );
}

/**
 * 🔒 Every Deal the list is attached to, each a link to its Deal, beside the
 * pill (decision 1091); a list on no Deal says so.
 */
function BomDealLinks({ deals }: { deals: readonly { dealId: string; dealName: string }[] }) {
  if (deals.length === 0) return <span className={styles.dealLinks} data-bom-deals="none">{t('deal.boms.notAttached')}</span>;
  return (
    <span className={styles.dealLinks} data-bom-deals={String(deals.length)}>
      {deals.map((deal) => (
        <Link key={deal.dealId} to={`/deals/${deal.dealId}`} className={styles.dealLink} data-bom-deal={deal.dealId}>
          {deal.dealName}
        </Link>
      ))}
    </span>
  );
}

/** 🔒 The list's status on Details, Draft or Final, written as it is picked (decision 1091). */
function BomStatusControl({ bomId, status, editable, resolve, apiBaseUrl = '', onSaved, onError }: { bomId: string; status: string; editable: boolean; resolve: StatusResolver; apiBaseUrl?: string; onSaved: (message: string) => void; onError: (message: string) => void }) {
  const [saving, setSaving] = useState(false);
  const pick = async (next: string) => {
    if (next === status) return;
    setSaving(true);
    try {
      const answer = await patch(BOM_PATH(bomId), { status: next }, apiBaseUrl);
      if (!answer.ok) return onError(answer.message);
      onSaved(t('deal.boms.statusSaved'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div data-bom-status-control={bomId}>
      <Select
        size={Size.Small}
        label={t('deal.boms.status')}
        value={status}
        onChange={(next) => void pick(next)}
        options={BOM_STATUSES.map((value) => ({ value, label: resolve('BOMStatus', value)?.label ?? value }))}
        disabled={!editable || saving}
        dataAttribute={{ 'data-bom-status': bomId }}
      />
    </div>
  );
}

/**
 * The list's description, typed on the list's own page (decision 0609). It
 * reads as a line until the pencil is pressed, so the page opens on what the
 * list says rather than on a form. 🔨 It writes the `BOM` row itself, which is
 * the write that took the row out of piece 7's exclusions (decision 0610).
 */
function BomDescription({ bomId, description, editable, apiBaseUrl = '', onSaved, onError }: { bomId: string; description: string | null; editable: boolean; apiBaseUrl?: string; onSaved: (message: string) => void; onError: (message: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(description ?? '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const answer = await patch(BOM_PATH(bomId), { description: draft.trim() || null }, apiBaseUrl);
      if (!answer.ok) return onError(answer.message);
      setEditing(false);
      onSaved(t('deal.boms.detailsSaved'));
    } finally {
      setSaving(false);
    }
  };
  if (!editing) {
    return (
      <p className={styles.description} data-bom-page-description={bomId}>
        <span>{description ?? t('deal.boms.noDescription')}</span>
        {editable ? (
          <button type="button" className={styles.descriptionEdit} onClick={() => { setDraft(description ?? ''); setEditing(true); }} data-bom-description-edit={bomId}>
            {t('deal.boms.editDetails')}
          </button>
        ) : null}
      </p>
    );
  }
  return (
    <div className={styles.description} data-bom-page-description={bomId}>
      <TextInput chars={60} label={t('deal.boms.description')} value={draft} onChange={setDraft} placeholder={t('deal.boms.descriptionPlaceholder')} name="bomDescription" disabled={saving} />
      <Button size={Size.Small} color={Color.Navy} text={[t('deal.boms.saveDetails')]} actions={[() => void save()]} disabled={saving} />
      <Button size={Size.Small} color={Color.Inactive} text={[t('modal.cancel')]} actions={[() => setEditing(false)]} disabled={saving} />
    </div>
  );
}

/**
 * The Deal's old address for a list, `/deals/:dealId/boms/:bomId`. 🔒 It
 * redirects to the list's one home, `/boms/:bomId`, naming the Deal in the
 * query and keeping any `mode`, so a saved link still opens the list (decision
 * 1092, amending 0565 and 1052).
 */
export function DealBomRoute() {
  const { dealId, bomId } = useParams<{ dealId: string; bomId: string }>();
  const [params] = useSearchParams();
  if (!bomId) return null;
  const kept = new URLSearchParams(params);
  if (dealId) kept.set('deal', dealId);
  return <Navigate to={`${LIST_PAGES.BOM.route}/${bomId}?${kept.toString()}`} replace />;
}
